<template>
  <div class="calendar-app__event-modal">
    <div
      class="calendar-app__modal modal__wrapper"
      :class="{ 'modal-open': modalVisible }"
      ref="modalwrapper"
    >
      <div class="calendar-app__modal-content modal" ref="modal">
        <article class="results-list__item">
          <div class="results-list__item-img" v-if="event.extendedProps.image">
            <picture tabindex="-1">
              <source srcset="" sizes="100vw" />
              <img
                :src="event.extendedProps.image"
                :alt="event.extendedProps.image_alt"
              />
            </picture>
          </div>
          <div class="results-list__content">
            <span
              class="results-list__tophat u-secondary-dark"
              v-if="event.extendedProps.event_type_friendly.length"
              >{{ eventTypeFriendly(event.extendedProps) }}</span
            >
            <a
              @click.prevent="navigationCheck"
              @keydown="setKeyboardEvent"
              :href="event.extendedProps.path"
              ref="eventLink"
              class="is-gated js-modal-trigger results-list__title-link"
            >
              <h3
                class="
                  results-list__title
                  u-primary-very-dark
                  u-hover-tertiary-dark
                  u-medium-title
                  u-sans-serif
                  u-sans-serif-title
                  u-type-28
                  u-type-until-bp-small-24
                "
              >
                {{ event.title }}
              </h3>
            </a>
            <div class="results-list__event-meta">
              <div class="results-list__time">{{ dateTime }}</div>

              <div v-if="!isEyeOnEquityEvent(event.extendedProps)" class="results-list__locations">
                <div
                  class="results-list__locations-1"
                  v-if="event.extendedProps.location_1"
                  v-html="event.extendedProps.location_1"
                ></div>
                <div
                  class="results-list__locations-2 u-before-bg-primary-medium-dark"
                  v-if="event.extendedProps.location_2"
                  v-html="event.extendedProps.location_2"
                ></div>
              </div>
            </div>
            <div class="results-list__summary u-sans-serif u-type-16">
              {{ summary }}
            </div>
          </div>
        </article>
      </div>
      <button
        class="
          modal__close-btn
          u-primary-medium-dark u-hover-primary-medium-dark-2
        "
        @click="closeModal"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="36"
          height="36"
          viewBox="0 0 36 36"
          aria-hidden="true"
        >
          <g fill="none" fill-rule="nonzero">
            <circle cx="18" cy="18" r="18" fill="currentColor"></circle>
            <path
              fill="#FFFFFF"
              d="M19.806 27.414L19.806 19.806 27.414 19.806 27.414 16.194 19.806 16.194 19.806 8.586 16.194 8.586 16.194 16.194 8.586 16.194 8.586 19.806 16.194 19.806 16.194 27.414z"
              transform="rotate(45 18 18)"
            ></path>
          </g></svg
        >Close
      </button>
    </div>
    <DelegateModal
      v-if="!getIsAuthenticated"
      @closeDelegateModal="closeDelegateModal"
      :show-modal="showDelegateModal"
      :has-close-button="true"
      ref="delegateModal"
    ></DelegateModal>
  </div>
</template>

<script>
import DelegateModal from '@/components/DelegateModal';
import {mapGetters} from 'vuex';
import moment from 'moment';
import calendar from '@/mixins/calendar'

export default {
  name: 'CalendarModal',
  mixins: [calendar],
  components: { DelegateModal },
  props: {
    event: {
      type: Object,
      required: true,
    },
    showModal: {
      type: Boolean,
      required: true,
    },
    timeZone: {
      type: String,
      default: 'US/Eastern'
    },
    rawEvent: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showDelegateModal: false,
      modalVisible: this.showModal,
      forcedFocusable: null,
      firstFocusable: null,
      lastFocusable: null,
      delegateEvent: {}
    };
  },
  computed: {
    ...mapGetters({
      getIsAuthenticated: 'getIsAuthenticated',
    }),
    summary() {
      if (this.isEyeOnEquityEvent(this.event.extendedProps)) {
        return this.event.extendedProps.summary
      }

      let end_date_time
      let current_date_time
      if(this.event.extendedProps.start_date_and_time_1 === undefined) {
        let conclusion_date_and_time = moment(this.event.extendedProps.event_conclusion_date_and_time).add(1, 'days').format();
        end_date_time = new Date(conclusion_date_and_time);
        current_date_time = new Date();
      } else {
        end_date_time =  new Date(this.event.extendedProps.event_conclusion_date_and_time);
        current_date_time = new Date();
      }

      return end_date_time > current_date_time
        ? this.event.extendedProps.summary
        : 'This event has concluded.';
    },
    dateTime() {
      // Thursday, June 8, 2022, 3:00 - 4:30 PM EDT + 1 more event
      let date_time = '';
      if (this.hasTime()) {
        // Deal with Eye on Equity events first. They should never have a time
        // but we'll handle it anyway.
        if (this.isEyeOnEquityEvent(this.event.extendedProps)) {
          const eoeDateTime = this.hasEndDate()
            ? this.event.extendedProps.end_date_and_time_1
            : this.event.extendedProps.start_date_and_time_1

          // Return without the time or timezone.
          return moment(String(eoeDateTime.slice(0, 10))).format('LL');
        }
        if (this.hasEndDate()) {
          const isSameDay = this.isSameDay(
            this.event.extendedProps.start_date_and_time_1,
            this.event.extendedProps.end_date_and_time_1
          );
          if (isSameDay) {
            date_time +=
              moment(
                String(this.event.extendedProps.start_date_and_time_1)
              ).tz(this.timeZone).format('LLLL') +
              ' - ' +
              moment(
                String(this.event.extendedProps.end_date_and_time_1)
              ).tz(this.timeZone).format('LT') +
              ' ' +
              moment.tz(this.timeZone).zoneAbbr();
          } else {
            date_time +=
              moment(
                String(this.event.extendedProps.start_date_and_time_1)
              ).tz(this.timeZone).format('LLLL') +
              ' - ' +
              moment(
                String(this.event.extendedProps.end_date_and_time_1)
              ).tz(this.timeZone).format('LLLL') +
              ' ' +
              moment.tz(this.timeZone).zoneAbbr();
          }
        } else {
          date_time +=
            moment(
              String(this.event.extendedProps.start_date_and_time_1)
            ).tz(this.timeZone).format('LLLL') +
            ' ' +
            moment.tz(this.timeZone).zoneAbbr();
        }
      } else {
        date_time += moment(
          String(this.event.extendedProps.start_date_date_only_1.slice(0, 10))
        ).format('LL');
        if (this.hasEndDate()) {
          const isSameDay = this.isSameDay(
              this.event.extendedProps.start_date_date_only_1,
              this.event.extendedProps.end_date_and_time_1
          );
          if (!isSameDay) {
            date_time += ' - ' + moment(
                String(this.event.extendedProps.end_date_and_time_1.slice(0, 10))
            ).format('LL');
          }
        }
      }

      // If an event has more than one Date and Time paragraph,
      // we display some "+x more" to indicated that there are multiple days and sessions
      // date_time +=
      //   this.event.extendedProps.event_count > 1
      //     ? ' + ' +
      //       (this.event.extendedProps.event_count - 1) +
      //       ' more event' +
      //       (this.event.extendedProps.event_count > 2 ? 's' : '')
      //     : '';

      return date_time;
    },
  },
  mounted() {
    // console.dir(this.event.start)
    // console.dir(this.event.start.toLocaleDateString())
    // console.dir(this.event.start.toLocaleString())
    // console.dir(this.event.start.toDateString())

    document.addEventListener('keydown', (e) => {
      const html = document.querySelector('html');

      if (e.code == 'Escape' && html.classList.contains('no-scroll')) {
        this.closeModal();
      }
    });

    document.addEventListener('click', (e) => {
      if (e.defaultPrevented) return;

      const html = document.querySelector('html');

      if (
        !e.target.closest('.modal') &&
        html.classList.contains('no-scroll') &&
        !e.target.closest('.fc-event') &&
        !e.target.closest('button')
      ) {
        this.closeModal();
      }
    });
  },
  watch: {
    showModal(newVal) {
      this.modalVisible = newVal;

      // show modal
      if (newVal) {
        this.setKeyboardNav();
        this.openModal();
      } else {
        this.unsetKeyboardNav();
      }
    },
  },
  methods: {
    closeDelegateModal() {
      this.showDelegateModal = false;
      this.$refs.eventLink.focus();
    },
    setKeyboardEvent(e) {
      if (e.key === "Enter" || e.key === " ") {
        this.$refs.delegateModal.setEvent(e);
      }
    },
    navigationCheck() {
      if (this.event.extendedProps.is_ra) {
        // This is an RA event, so make sure they're authenticated.
        if (this.getIsAuthenticated) {
          // Authenticated. Close the modal, clear the nextUrl, and navigate.
          this.showDelegateModal = false;
          this.$store.commit('updateNextUrl', null);
          window.location.href = this.event.extendedProps.path;
        } else {
          // Unauthenticated. Show the modal and store the nextUrl.
          this.$store.commit('updateNextUrl', this.event.extendedProps.path);
          this.showDelegateModal = true;
        }
      } else {
        // Not an RA event; navigate right away.
        window.location.href = this.event.extendedProps.path;
      }
    },
    closeModal() {
      // only close this modal if the delegate modal is overlaying it
      if (this.showDelegateModal) {
        return;
      }

      const eventLink = this.rawEvent.jsEvent.target.closest('a');

      this.$emit('modalClosed');
      document.querySelector('html').classList.remove('no-scroll');

      this.forcedFocusable?.classList.remove("is-keyboard-focused")
      this.firstFocusable.classList.remove('is-keyboard-focused');

      // set focus back to event in calendar that was opened
      eventLink.focus();
    },
    openModal() {
      if (this.rawEvent.jsEvent) {
        if (
          this.rawEvent.jsEvent.key == ' ' ||
          this.rawEvent.jsEvent.key == 'Enter'
        ) {
          // wait for modal to attach to refs
          this.$nextTick(() => {
            // use this class to only show outline only when using keyboard navigation
            this.forcedFocusable?.classList.add("is-keyboard-focused")
            this.firstFocusable.classList.add('is-keyboard-focused');
          });
        }
      }
      document.querySelector('html').classList.add('no-scroll');
    },
    setKeyboardNav() {
      // wait for refs to attach
      this.$nextTick(() => {
        // attach event listeners to first and last focusable items
        const forcedFocusEl = this.$refs.modal.querySelector("[tabindex='-1']");
        const length = this.getFocuseableElements(
          this.$refs.modalwrapper
        ).length;
        const firstFocusEl = this.getFocuseableElements(this.$refs.modal)[0];

        // set first and forced focusable elements
        this.forcedFocusable = forcedFocusEl;
        this.forcedFocusable?.addEventListener(
          'keydown',
          this.handleFirstElementKeydown
        );

        this.firstFocusable = firstFocusEl;
        this.firstFocusable.addEventListener(
          'keydown',
          this.handleFirstElementKeydown
        );

        // set focus
        this.forcedFocusable
          ? this.forcedFocusable.focus()
          : this.firstFocusable.focus();

        this.lastFocusable = this.getFocuseableElements(
          this.$refs.modalwrapper
        )[length - 1];

        this.lastFocusable.addEventListener(
          'keydown',
          this.handleLastElementKeydown
        );
      });
    },
    handleFirstElementKeydown(e) {
      if (e.shiftKey && e.key === 'Tab') {
        e.preventDefault();
        this.lastFocusable.focus();
      }

      this.forcedFocusable
        ? this.forcedFocusable.classList.add('is-keyboard-focused')
        : this.firstFocusable.classList.add('is-keyboard-focused');
    },
    handleLastElementKeydown(e) {
      if (!e.shiftKey && e.key === 'Tab') {
        e.preventDefault();
        this.firstFocusable.focus();
      }
    },
    unsetKeyboardNav() {
      // remove event listeners
      this.forcedFocusable?.removeEventListener(
        'keydown',
        this.handleFirstElementKeydown
      );
      this.firstFocusable.removeEventListener(
        'keydown',
        this.handleFirstElementKeydown
      );
      this.lastFocusable.removeEventListener(
        'keydown',
        this.handleLastElementKeydown
      );
    },
    isSameDay(start, end) {
      // console.log( moment(String(start)).format('L') )
      // console.log( moment(String(end)).format('L') )
      return (
        moment(String(start)).format('L') === moment(String(end)).format('L')
      );
    },
    hasTime() {
      return this.event.extendedProps.start_date_and_time_1 !== undefined;
    },
    hasEndDate() {
      return this.event.extendedProps.end_date_and_time_1 !== undefined;
    },
    getFocuseableElements(element) {
      return element.querySelectorAll(
        'a, button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])'
      );
    },
  },
};
</script>

<style scoped>
picture:not(.is-keyboard-focused) {
  outline: 0;
}
</style>
